import React, { useEffect, useState } from 'react';
import { MainBar } from '@components/MainBar';
import { useStyles } from './styles/BasePage.styles';
import { useSelector } from 'react-redux';
import { AppRoutes } from '@consts/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import { isTokenExpired } from '@utils/helper';
import { logout } from '@features/redux/store';
import { refreshToken } from '@api/user';
import { LoadingScreen } from '@components/LoadingScreen';

import type { RootState } from '@features/redux/store';
import { checkPathHasPermission } from '@utils/permissions';

interface BasePageProps {
    children: React.ReactNode;
    noPadding?: boolean;
}

export const BasePage = ({ children, noPadding }: BasePageProps) => {
    const navigate = useNavigate();

    const classes = useStyles();

    const [tokenChecked, setTokenChecked] = useState(false);

    const {
        IdToken,
        RefreshToken,
        Persist,
        Roles: { role },
    } = useSelector((state: RootState) => state.auth);

    const { pathname } = useLocation();

    useEffect(() => {
        if (!IdToken) {
            navigate(AppRoutes.Login);
        }
    }, [IdToken]);

    useEffect(() => {
        if (!checkPathHasPermission(role, pathname as AppRoutes)) {
            navigate(pathname === AppRoutes.Insights ? AppRoutes.Login : AppRoutes.Insights);
        }
    }, [pathname, role]);

    useEffect(() => {
        // Skip if already logged out (IdToken is empty/null)
        if (!IdToken) {
            return;
        }

        const expired = isTokenExpired(IdToken);
        if (expired && Persist && RefreshToken) {
            refreshToken(RefreshToken);
        } else if (expired) {
            logout();
        } else {
            setTokenChecked(true);
        }
    }, [IdToken, Persist, RefreshToken]);

    return tokenChecked && IdToken ? (
        <div className={noPadding ? classes.rootNoPadding : classes.root}>
            <MainBar />
            <main>{children}</main>
        </div>
    ) : (
        <LoadingScreen active={true} />
    );
};
